/* VIEW — OVERVIEW. Exact port of code.txt OverviewView + InsightCard; the recharts area
   chart becomes mountAreaChart(). */

import { Card, Lbl, Metric, SectionTitle, BtnNeutral, T, TIER_STYLE, SEARCH_LABEL_C, MONO, FONT, fmt, AioLabel, escapeHtml } from "../../ui.js";
import { searchLabel, aiLabel } from "@agf/analyzer";
import { titleCase } from "@agf/analyzer";
import { mountAreaChart } from "../../areachart.js";

function InsightCard({ kicker, kickerColor, topic, ai, lines, cta, attrs }) {
  return Card(`
    ${Lbl(kicker, `color:${kickerColor}`)}
    <div style="font-size:16.5px;font-weight:800;letter-spacing:-0.3px;color:${T.ink};margin:2px 0 12px">${topic}</div>
    <div style="display:flex;flex-direction:column;gap:7px;flex:1">
      ${lines.map(([k, v, c]) => `
        <div style="display:flex;justify-content:space-between;align-items:center">
          <span style="font-size:12px;color:${T.muted};font-weight:600">${k}</span>
          <span style="font-size:12.5px;font-weight:800;color:${c}">${v}</span>
        </div>`).join("")}
    </div>
    ${BtnNeutral(cta, { small: true, attrs, style: "margin-top:14px;align-self:flex-start" })}`,
  { ai });
}

export function renderOverview(el, { R, openTopic, goto }) {
  const s = R.site;
  const topOpps = R.opportunities.slice(0, 2);
  const topWin = R.winners[0];
  el.innerHTML = `
    ${Card(`
      <div style="font-size:17px;font-weight:800;letter-spacing:-0.3px;margin-bottom:6px">Your AI Search Performance</div>
      <p style="font-size:13.5px;color:${T.sub};line-height:1.6;margin:0;max-width:620px">
        <b style="font-family:${MONO};color:${T.violet}">${s.oppCount}</b> topics look strong in Google Search but weak in AI results — 
        <b style="font-family:${MONO};color:${T.ink}">${fmt(s.oppImp)}</b> impressions and <b style="font-family:${MONO};color:${T.ink}">${fmt(s.oppClicks)}</b> clicks at stake.
      </p>`,
    { style: `background-image:linear-gradient(110deg, ${T.violetSoft}, transparent 60%);margin-bottom:16px` })}
    ${R.meta.serpDegraded ? Card(`
      <div style="display:flex;gap:10px;align-items:flex-start">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${T.amber}" stroke-width="2.2" stroke-linecap="round" style="flex-shrink:0;margin-top:1px"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16.5" x2="12" y2="16.6"/></svg>
        <div>
          <div style="font-weight:800;font-size:13px;margin-bottom:3px">The live SERP check did not finish</div>
          <div style="font-size:12.5px;color:${T.sub};line-height:1.6">This report is based on your Search Console data only, so the AI Overview columns are not filled in. Reason: ${escapeHtml(String(R.meta.serpDegraded))}. Running the analysis again usually clears it.</div>
        </div>
      </div>`,
      { style: `background:${T.amberSoft};border-color:${T.amber}33;margin-bottom:16px;padding:14px 16px` }) : ""}
    ${R.meta.llm?.summary ? Card(`
      ${Lbl(AioLabel("AI summary", 11), `color:${T.magenta}`)}
      <p style="font-size:13px;color:${T.sub};line-height:1.65;margin:6px 0 0">${escapeHtml(R.meta.llm.summary)}</p>`,
      { ai: true, style: "margin-bottom:16px" }) : ""}
    <div style="display:grid;grid-template-columns:repeat(4, minmax(0,1fr));gap:12px;margin-bottom:20px">
      ${Card(`${Lbl("Search visibility")}<div style="font-size:20px;font-weight:800;color:${SEARCH_LABEL_C[s.searchLabel]}">${s.searchLabel}</div>`, { style: "padding:14px 16px" })}
      ${Card(`${Lbl("AI visibility", `color:${T.magenta}`)}<div style="font-size:20px;font-weight:800;color:${T.magenta}">${s.aiLabel}</div>`, { ai: true, style: "padding:14px 16px" })}
      ${Card(`${Lbl("AI opportunities")}${Metric(s.oppCount, { size: 20, color: T.violet })}`, { style: "padding:14px 16px", attrs: `data-goto="opps" class="click-card"` })}
      ${Card(`${Lbl("High priority")}${Metric(s.highCount, { size: 20, color: s.highCount ? T.red : T.green })}`, { style: "padding:14px 16px", attrs: `data-goto="opps" class="click-card"` })}
    </div>

    ${SectionTitle("What may need your attention")}
    <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px;margin-bottom:24px">
      ${topOpps.slice(0, 1).map(t => InsightCard({
        kicker: "Biggest potential opportunity", kickerColor: T.red,
        topic: t.name,
        ai: false,
        lines: [
          ["Google Search", searchLabel(t.searchScore), SEARCH_LABEL_C[searchLabel(t.searchScore)]],
          ["AI visibility", aiLabel(t.aiScore), T.magenta],
          ["Potential", t.tier === "VERY HIGH" ? "Very High" : titleCase(t.tier.toLowerCase()), TIER_STYLE[t.tier].c],
        ],
        cta: "See why",
        attrs: `data-open-topic="${t.id}"`,
      })).join("")}
      ${topWin ? InsightCard({
        kicker: "AI winner", kickerColor: T.magenta, topic: topWin.name,
        ai: true,
        lines: [
          ["Google Search", topWin.searchScore == null ? "\u2014" : searchLabel(topWin.searchScore), topWin.searchScore == null ? T.muted : SEARCH_LABEL_C[searchLabel(topWin.searchScore)]],
          ["AI visibility", aiLabel(topWin.aiScore), T.magenta],
          ["Worth repeating", "Likely", T.green],
        ],
        cta: "See why it works",
        attrs: `data-goto-winner="${topWin.id}"`,
      }) : ""}
    </div>

    ${SectionTitle("AI visibility trend", { right: R.aiTrend ? "recent half vs. previous half" : "" })}
    ${R.aiTrend ? Card(`
      <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.1fr);gap:20px;align-items:center">
        <div>
          <div style="font-weight:800;font-size:14.5px;margin-bottom:6px">
            ${R.aiTrend.dir === "up" ? `Your AI visibility is growing (+${Math.round(R.aiTrend.delta * 100)}%)`
              : R.aiTrend.dir === "down" ? `Your AI visibility is slipping (${Math.round(R.aiTrend.delta * 100)}%)`
              : `Your AI visibility is holding steady`}
          </div>
          <div style="font-size:12.5px;color:${T.sub};line-height:1.55;margin-bottom:6px">
            <b style="color:${T.ink}">Why it matters:</b> 
            ${R.aiTrend.dir === "down"
              ? "Google is surfacing your site less often inside AI Overviews and AI Mode than it did earlier in this period."
              : R.aiTrend.dir === "up"
                ? "Google is surfacing your site more often inside AI Overviews and AI Mode than earlier in this period."
                : "Your presence inside AI Overviews and AI Mode hasn't meaningfully changed across this period."}
          </div>
          <div style="font-size:12.5px;color:${T.sub};line-height:1.55">
            <b style="color:${T.ink}">What to do:</b> 
            ${R.aiTrend.dir === "down"
              ? "Start with your highest-priority opportunities — recovering strong pages usually moves this line fastest."
              : "Work through the Opportunities list to convert more of your Search strength into AI visibility."}
          </div>
        </div>
        <div>
          <div id="trend-chart"></div>
          <div style="font-size:11px;color:${T.muted};margin-top:2px">Daily Generative AI impressions · ${R.meta.rangeLabel}</div>
        </div>
      </div>`, { ai: true }) : Card(`
      <div style="font-weight:700;font-size:13px;margin-bottom:3px">No trend data yet</div>
      <div style="font-size:12.5px;color:${T.sub}">Add the chart export from the Generative AI report (a small Date + Impressions CSV) and we'll show whether your AI visibility is growing or slipping.</div>`,
    { style: "padding:16px 18px" })}
  `;

  if (R.aiTrend) {
    mountAreaChart(el.querySelector("#trend-chart"), R.aiTrend.series.map(x => ({ d: String(x.d).slice(5), full: x.d, v: x.v })));
  }

  /* wiring */
  el.querySelectorAll("[data-open-topic]").forEach(b => b.addEventListener("click", () => openTopic(b.dataset.openTopic)));
  el.querySelectorAll("[data-goto-winner]").forEach(b => b.addEventListener("click", () => goto("winners", b.dataset.gotoWinner)));
  el.querySelectorAll("[data-goto]").forEach(b => b.addEventListener("click", () => goto(b.dataset.goto)));
}
